import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';

interface BoletoButtonDebugProps {
  invoiceId: string;
}

const BoletoButtonDebug: React.FC<BoletoButtonDebugProps> = ({ invoiceId }) => {
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runDebugCheck = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔍 Starting boleto debug check for invoice:', invoiceId);
      
      // Test 1: Check boleto eligibility
      console.log('📋 Step 1: Checking boleto eligibility');
      const eligibilityResponse = await apiService.axiosInstance.get(`/api/v1/boleto/check/${invoiceId}`);
      console.log('✅ Eligibility response:', eligibilityResponse.data);
      
      // Test 2: Try to create boleto (without force)
      console.log('📋 Step 2: Attempting to create boleto');
      try {
        const createResponse = await apiService.axiosInstance.post(`/api/v1/boleto/create/${invoiceId}`, {
          notification_url: 'https://your-domain.com/api/v1/boleto/webhook/epag',
          force_create: false
        });
        console.log('✅ Create response:', createResponse.data);
      } catch (createError: any) {
        console.log('⚠️ Create error (expected if boleto exists):', createError.response?.data);
      }
      
      // Test 3: Get invoice details
      console.log('📋 Step 3: Getting invoice details');
      const invoiceResponse = await apiService.axiosInstance.get(`/api/v1/invoices/${invoiceId}`);
      console.log('✅ Invoice response:', invoiceResponse.data);
      
      setDebugInfo({
        eligibility: eligibilityResponse.data,
        invoice: invoiceResponse.data,
        timestamp: new Date().toLocaleString() // Use locale-specific time string
      });
      
    } catch (err: any) {
      console.error('❌ Debug check failed:', err);
      setError(err.response?.data?.detail || err.message || 'Debug check failed');
    } finally {
      setLoading(false);
    }
  }, [invoiceId]);

  useEffect(() => {
    if (invoiceId) {
      runDebugCheck();
    }
  }, [invoiceId, runDebugCheck]);

  return (
    <div style={{ 
      border: '1px solid #ddd', 
      padding: '15px', 
      margin: '10px 0', 
      borderRadius: '5px',
      backgroundColor: '#f8f9fa'
    }}>
      <h4>🔧 Boleto Debug Information</h4>
      
      <button 
        onClick={runDebugCheck} 
        disabled={loading}
        style={{ marginBottom: '10px' }}
        className="btn btn-sm btn-info"
      >
        {loading ? 'Running Debug...' : 'Refresh Debug Info'}
      </button>
      
      {error && (
        <div style={{ color: 'red', marginBottom: '10px' }}>
          <strong>Error:</strong> {error}
        </div>
      )}
      
      {debugInfo && (
        <div>
          <h5>Eligibility Check:</h5>
          <pre style={{ fontSize: '12px', backgroundColor: '#fff', padding: '10px', overflow: 'auto' }}>
            {JSON.stringify(debugInfo.eligibility, null, 2)}
          </pre>
          
          <h5>Invoice Details:</h5>
          <pre style={{ fontSize: '12px', backgroundColor: '#fff', padding: '10px', overflow: 'auto' }}>
            {JSON.stringify({
              invoice_id: debugInfo.invoice.invoice_id,
              currency: debugInfo.invoice.currency,
              invoice_status: debugInfo.invoice.invoice_status,
              customer_id: debugInfo.invoice.customer_id,
              total_amount: debugInfo.invoice.total_amount
            }, null, 2)}
          </pre>
          
          <small style={{ color: '#666' }}>
            Last updated: {debugInfo.timestamp}
          </small>
        </div>
      )}
    </div>
  );
};

export default BoletoButtonDebug;